import { atom, useAtom } from "jotai";
import { atomFamily } from "jotai/utils";
import { useCallback, useMemo } from "react";

import { Updater } from "@headless-tree/core";

/**
 * Expansion state, namespaced per tree. Not persisted yet; when it is, this is the only place
 * that changes — swap the inner atom for `atomWithStorage`.
 */
const expansionAtomFamily = atomFamily((_treeId: string) => atom<string[]>([]));

export function useTreeExpansion(treeId: string) {
  const [expandedItems, setExpandedItems] = useAtom(
    expansionAtomFamily(treeId),
  );

  /** Shaped as headless-tree's `SetStateFn<string[]>`. */
  const setExpandedItemsFn = useCallback(
    (updaterOrValue: Updater<string[]>) => {
      setExpandedItems((prev) =>
        typeof updaterOrValue === "function"
          ? updaterOrValue(prev)
          : updaterOrValue,
      );
    },
    [setExpandedItems],
  );

  const expand = useCallback(
    (ids: string[]) => {
      if (ids.length === 0) return;
      setExpandedItems((prev) => {
        const next = new Set(prev);
        let changed = false;
        for (const id of ids) {
          if (!next.has(id)) {
            next.add(id);
            changed = true;
          }
        }
        return changed ? Array.from(next) : prev;
      });
    },
    [setExpandedItems],
  );

  const collapseAll = useCallback(() => {
    setExpandedItems((prev) => (prev.length === 0 ? prev : []));
  }, [setExpandedItems]);

  return useMemo(
    () => ({
      expandedItems,
      setExpandedItems: setExpandedItemsFn,
      expand,
      collapseAll,
    }),
    [expandedItems, setExpandedItemsFn, expand, collapseAll],
  );
}
